import { apiFetch } from './client';

// Module keys — mirror internal/services/websites/service.go constants.
// Keep in lockstep with the backend or the wizard will silently drop
// toggles the server doesn't understand.
export const WEBSITE_MODULES = [
  'news',
  'schedule',
  'teams',
  'sponsors',
  'about',
  'contact',
  'cta_membership',
  'events',
] as const;

export type WebsiteModule = (typeof WEBSITE_MODULES)[number];

export const WEBSITE_TEMPLATES = ['classic', 'cover', 'magazine', 'siderail'] as const;
export type WebsiteTemplate = (typeof WEBSITE_TEMPLATES)[number];

export type WebsiteStatus = 'draft' | 'published';

// SeedContent mirrors the Go service's SeedContent struct.
export interface SeedContent {
  news?: SeedNews[];
  about?: string;
  contact?: SeedContact;
  events?: SeedEvent[];
  sponsors?: Partner[];
  featured_tiles?: FeaturedTile[];
  // Multilingual page copy. source_language is the language the canonical
  // about/events free-text is written in (empty = platform default);
  // translations holds per-language variants, keyed by platform language
  // code, that the public site renders with per-field source fallback.
  source_language?: string;
  translations?: Record<string, SeedContentTranslation>;
}

// SeedContentTranslation mirrors websites.SeedContentTranslation — one
// language variant of the editable free-text page copy (about + events).
export interface SeedContentTranslation {
  about?: string;
  events?: SeedEvent[];
}

// FeaturedTile mirrors websites.FeaturedTile. The kind field decides
// what `ref` points at: a team id, a custom-page slug, a news slug, or
// an absolute URL. Label + image_object_key override defaults pulled
// from the linked entity. Only the magazine template renders this list
// today; other templates ignore the field.
export type FeaturedTileKind = 'team' | 'page' | 'news' | 'url';

export interface FeaturedTile {
  label?: string;
  image_object_key?: string;
  kind: FeaturedTileKind;
  ref?: string;
}

export const FEATURED_TILE_KINDS: readonly FeaturedTileKind[] = [
  'team',
  'page',
  'news',
  'url',
] as const;

export const MAX_FEATURED_TILES = 6;

export interface SeedNews {
  slug: string;
  title: string;
  summary?: string;
  body?: string;
  date?: string; // YYYY-MM-DD
}

export interface SeedContact {
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  postal?: string;
}

export interface SeedEvent {
  title: string;
  summary?: string;
  date?: string;
  location?: string;
}

export interface Website {
  id: string;
  name: string;
  // URL slug under /c/<slug>. Each website owns its own slug so multiple
  // sites per org (different sports, different age groups) can be live
  // in parallel. The first site usually inherits the org slug verbatim;
  // sub-brand sites get user-chosen suffixes like "<orgslug>-basketball".
  slug: string;
  template: WebsiteTemplate;
  hero_seed: number;
  enabled_modules: WebsiteModule[];
  seed_content: SeedContent;
  // team_ids is the per-site team allowlist. Empty array means "this
  // site shows no teams". Populated narrows the eligible pool
  // (members.team rows with public_visibility=TRUE) to this set so the
  // football site doesn't surface basketball teams and vice versa.
  team_ids: string[];
  // Multilingual config. supported_languages is the set of platform
  // language codes this site renders in; primary_language is the one served
  // at the bare URL and shown by default. primary_language is always a
  // member of supported_languages.
  supported_languages: string[];
  primary_language: string;
  status: WebsiteStatus;
  published_at?: string;
  // scheduled_publish_at is the future wall-clock the worker uses to
  // auto-publish a draft. Cleared on every publish / unpublish; only
  // ever non-null when status === 'draft'.
  scheduled_publish_at?: string;
  created_at: string;
  updated_at: string;
  // Short-lived (30 min) HMAC token bound to (orgID, this websiteID).
  // Lets the wizard iframe /c/<slug>?preview=<token>. Empty when the
  // server's preview signer isn't configured.
  preview_token?: string;
  // Design-mode payload — populated whether or not design mode is on.
  // Renderer ignores these when design_mode_enabled is false.
  design_mode_enabled: boolean;
  site_entries: SiteEntry[];
  theme_overrides: ThemeOverrides;
  // has_pending_changes is true when the row carries unpublished edits
  // that the user hasn't flushed via Publish yet. The dashboard pill
  // renders "Live · unpublished edits" / "Draft · unpublished edits"
  // off this flag; the editor surfaces a "Discard changes" action.
  has_pending_changes: boolean;
}

// Design-mode shapes. Keep in lockstep with the Go-side types in
// internal/services/websites/design.go — the JSON shape is canonical.

// SiteEntry kinds. system = built-in route, page = admin-defined custom
// page, external = absolute URL. The discriminator drives which optional
// field carries the entry's payload.
export const SITE_ENTRY_KINDS = ['system', 'page', 'external'] as const;
export type SiteEntryKind = (typeof SITE_ENTRY_KINDS)[number];

// SystemRoute values for kind=system entries. The renderer resolves
// these against the org slug to produce the link target.
export const SYSTEM_ROUTES = [
  'home',
  'schedule',
  'teams',
  'partners',
  'about',
  'contact',
  'news_index',
] as const;
export type SystemRoute = (typeof SYSTEM_ROUTES)[number];

// SiteEntry is one ordered row in the unified site-structure editor.
// id is the stable client-side identifier — required and unique. The
// SPA generates one (uuidv7-ish) when adding an entry; the server
// rejects entries with empty or duplicate ids.
export interface SiteEntry {
  id: string;
  label: string;
  kind: SiteEntryKind;
  system_route?: SystemRoute;
  page?: PageContent;
  external_url?: string;
  show_in_nav: boolean;
}

export const BLOCK_TYPES = [
  'heading',
  'paragraph',
  'image',
  'button',
  'gallery',
  'embed',
] as const;
export type BlockType = (typeof BLOCK_TYPES)[number];

export interface Block {
  type: BlockType;
  text?: string;
  href?: string;
  imageKey?: string;
  imageKeys?: string[];
  src?: string;
  caption?: string;
  // Desktop layout width. 1 (or omitted) = full, 2 = half, 3 = third.
  // Two consecutive non-full blocks render side-by-side via flex.
  columns?: 1 | 2 | 3;
  // Vertical alignment against row-mates. Only meaningful when the
  // block is half/third width and sits next to another non-full block.
  // Empty / 'top' = default (align-items: flex-start on the row).
  alignSelf?: 'top' | 'middle' | 'bottom';
}

// PageContent is the editable payload for a kind=page SiteEntry.
// Rendered at /c/{slug}/p/{slug}.
export interface PageContent {
  slug: string;
  title: string;
  blocks: Block[];
  // Multilingual page copy. source_language is the language the canonical
  // title/blocks are written in (empty = platform default); translations
  // holds per-language variants the public renderer substitutes with
  // per-field source fallback. The slug is never translated.
  source_language?: string;
  translations?: Record<string, PageContentTranslation>;
}

// PageContentTranslation mirrors websites.PageContentTranslation — one
// language variant of a custom page (title + ordered blocks). Either field
// may be empty; the renderer falls back to the source per field.
export interface PageContentTranslation {
  title?: string;
  blocks?: Block[];
}

export interface ThemeOverrides {
  primaryColor?: string;
  accentColor?: string;
  headingFont?: string;
  bodyFont?: string;
  heroImageKey?: string;
}

export interface CuratedFont {
  key: string;
  name: string;
  category: 'heading' | 'body' | 'both';
  cssStack: string;
  fileBase?: string;
}

// Partner mirrors the Go-side SeedSponsor struct — the field set the
// Partners design tab edits. Logo is required (the design editor
// enforces it before save); primary partners are surfaced in the site
// header strip and rendered larger on the dedicated /partners page.
export interface Partner {
  logo: string;
  url?: string;
  primary?: boolean;
}

export interface WebsiteDesign {
  website_id: string;
  // The website's URL slug — editable from the Teams/General tab so an
  // admin can rename `/c/<slug>` after the wizard ran.
  slug: string;
  design_mode_enabled: boolean;
  // site_entries: when entries_are_auto=true the server returns the
  // synthesized seed list (one entry per enabled module) so the editor
  // opens prepopulated instead of empty. Saving promotes the seed to an
  // explicit override. Empty array on the wire = "fall back to auto".
  site_entries: SiteEntry[];
  entries_are_auto: boolean;
  enabled_modules: WebsiteModule[];
  theme_overrides: ThemeOverrides;
  // default_hero_url is the stock-photo URL the public renderer falls
  // back to when theme_overrides.heroImageKey is empty. The design
  // editor surfaces it as a "this is what visitors currently see"
  // preview inside the hero override picker.
  default_hero_url: string;
  // stock_image_urls is the resolved CDN URL for every "stock.<kind>"
  // and "stock.<kind>.<n>" reference the AI page generator emits in
  // image blocks. The editor uses this map to render previews for
  // stock placeholders without recomputing the seed math client-side.
  stock_image_urls: Record<string, string>;
  curated_fonts: CuratedFont[];
  partners: Partner[];
  // team_ids is the per-site team allowlist edited by the Teams tab.
  // Empty array means the site shows no teams. The full eligible pool
  // (org-wide public teams) comes from /teams.
  team_ids: string[];
  // featured_tiles is the magazine-template spotlight grid edited by
  // the Featured tab. Empty array means "use template defaults".
  featured_tiles: FeaturedTile[];
  // Multilingual config edited by the settings tab. supported_languages is
  // the set of platform languages the site renders in; primary_language is
  // the one served at the bare URL.
  supported_languages: string[];
  primary_language: string;
  // Effective (live + pending) source page copy, surfaced read-only so the
  // Translations tab can show what's being translated. about/events are the
  // source-language text; content_source_language is the language they're
  // written in; content_translations holds the per-language variants the
  // Translations tab edits via setDesignContentTranslations.
  about: string;
  events: SeedEvent[];
  content_source_language: string;
  content_translations: Record<string, SeedContentTranslation>;
}

// SaveWebsiteInput is the payload for both create + update. Name +
// slug are required on create; on update an empty string for either
// means "leave unchanged". team_ids is a nullable array: `undefined`
// means "leave unchanged"; an explicit `[]` clears the allowlist.
export interface SaveWebsiteInput {
  name: string;
  slug?: string;
  template: WebsiteTemplate;
  hero_seed: number;
  enabled_modules: WebsiteModule[];
  seed_content?: SeedContent;
  team_ids?: string[];
  // Multilingual config. On create both are sent; the backend defaults them
  // to the org's language when omitted. On update, omitting both leaves the
  // language config unchanged (they must change together).
  supported_languages?: string[];
  primary_language?: string;
}

interface ListWebsitesResponse {
  websites: Website[];
}

// listWebsites returns every non-deleted website variant for the org,
// newest-published first then by updated_at. Used by the dashboard's
// table view.
export async function listWebsites(
  token: string | null,
  slug: string,
): Promise<Website[]> {
  const res = await apiFetch<ListWebsitesResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites`,
    { token },
  );
  return res.websites;
}

// getWebsite fetches one website by id. Throws ApiError 404 when the
// row doesn't exist (or has been soft-deleted, or belongs to another org).
export async function getWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}`,
    { token },
  );
}

// createWebsite inserts a new draft row. Used by the dashboard's "Add"
// button to start a fresh wizard run without touching existing websites.
export async function createWebsite(
  token: string | null,
  slug: string,
  input: SaveWebsiteInput,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites`,
    { method: 'POST', body: input, token },
  );
}

// updateWebsite edits an existing row. Preserves status (draft stays
// draft, published stays published) — separate publish/unpublish calls
// flip status.
export async function updateWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
  input: SaveWebsiteInput,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}`,
    { method: 'PUT', body: input, token },
  );
}

export async function publishWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/publish`,
    { method: 'POST', token },
  );
}

export async function unpublishWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/unpublish`,
    { method: 'POST', token },
  );
}

// discardWebsitePending throws away unpublished edits staged in
// pending_payload and reverts the editor to the last-published state.
// No-op on the server when there are no staged edits; the SPA still
// reloads so the "unpublished edits" pill disappears.
export async function discardWebsitePending(
  token: string | null,
  slug: string,
  websiteID: string,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/discard-pending`,
    { method: 'POST', token },
  );
}

export async function deleteWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
): Promise<void> {
  return apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}`,
    { method: 'DELETE', token },
  );
}

// schedulePublishWebsite stamps a future timestamp on a draft. The
// websites.scheduled_publish worker auto-publishes when the wall clock
// catches up. publishAt is RFC 3339 (`new Date(...).toISOString()`).
export async function schedulePublishWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
  publishAt: string,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/schedule-publish`,
    { method: 'POST', body: { publish_at: publishAt }, token },
  );
}

export async function cancelScheduledPublishWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/schedule-publish`,
    { method: 'DELETE', token },
  );
}

// duplicateWebsite creates a draft copy of an existing variant.
// Useful for A/B testing a new design without touching the live site.
// New row starts as draft regardless of source status; nav / pages /
// theme / modules are copied verbatim.
export async function duplicateWebsite(
  token: string | null,
  slug: string,
  websiteID: string,
  name: string,
): Promise<Website> {
  return apiFetch<Website>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/duplicate`,
    { method: 'POST', body: { name }, token },
  );
}

export async function getSeedDefaults(
  token: string | null,
  slug: string,
  websiteID: string,
  locale: string,
): Promise<SeedContent> {
  return apiFetch<SeedContent>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/seed-defaults?locale=${encodeURIComponent(locale)}`,
    { method: 'POST', token },
  );
}

export interface LogoPalette {
  primary: string;
  accent: string;
  muted: string;
}

// analyzeLogo asks the backend to extract brand colors from the logo
// at the given media object_key. Pure pixel clustering server-side —
// no AI dep — so latency is microseconds. Throws ApiError 503 when
// the analyzer isn't configured (dev environments without storage).
export async function analyzeLogo(
  token: string | null,
  slug: string,
  objectKey: string,
): Promise<LogoPalette> {
  return apiFetch<LogoPalette>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/analyze-logo`,
    { method: 'POST', body: { object_key: objectKey }, token },
  );
}

// BrandImport is the advisory result of probing a club's existing external
// site — colors + title/description the onboarding wizard uses to pre-fill a
// new website draft. Colors are "#rrggbb" or empty.
export interface BrandImport {
  primary_color: string;
  accent_color: string;
  muted_color: string;
  title: string;
  description: string;
  logo_found: boolean;
}

// importBrand asks the backend to fetch an external URL and extract brand
// hints (read-only probe, no mutation). Throws ApiError 503 when the fetcher
// isn't wired, or 400 (brand_import_failed) for an unreachable/blocked/invalid
// URL — callers should catch and let the user continue with a fresh site.
export async function importBrand(
  token: string | null,
  slug: string,
  url: string,
): Promise<BrandImport> {
  return apiFetch<BrandImport>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/import-brand`,
    { method: 'POST', body: { url }, token },
  );
}

// publicURL builds the absolute URL a public website lives at.
//
// When publicSitesBaseDomain is set (production: "sportadmin.io"), the
// URL uses the public-host form: `https://<websiteSlug>.<baseDomain>`.
// That's the canonical home of the public site — separate origin from
// the API/SPA, served by the host-based dispatcher in cmd/api/main.go,
// and the only form that satisfies the SPA's `frame-src
// https://*.sportadmin.io` CSP for iframe previews.
//
// When publicSitesBaseDomain is empty (dev without that env set), we
// fall back to the legacy `/c/<slug>` path on the API host so local
// development works without DNS / Caddy. The fallback iframe will be
// blocked in any browser that enforces the SPA's CSP — fine for SPA
// dev where Vite serves no CSP at all.
//
// websiteSlug is the WEBSITE slug (not the org slug). Each website
// carries its own slug.
export function publicURL(apiURL: string, baseDomain: string, websiteSlug: string): string {
  if (baseDomain) {
    return `https://${encodeURIComponent(websiteSlug)}.${baseDomain}`;
  }
  const base = apiURL.replace(/\/api\/?$/, '');
  return `${base}/c/${encodeURIComponent(websiteSlug)}`;
}

// websitePreviewURL builds the iframe-able URL that renders a specific
// draft. The preview token in the URL is bound to (orgID, websiteID) so
// the public handler always knows which row to serve.
export function websitePreviewURL(
  apiURL: string,
  baseDomain: string,
  websiteSlug: string,
  previewToken: string,
): string {
  return `${publicURL(apiURL, baseDomain, websiteSlug)}?preview=${encodeURIComponent(previewToken)}`;
}

// --- Design mode -----------------------------------------------------

const designBase = (slug: string, websiteID: string): string =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/design`;

// getWebsiteDesign hydrates the design-mode editor in one round-trip.
// Includes the curated font catalog so the picker doesn't need a
// second call. `locale` controls the language of the seeded auto-nav
// labels — pass the SPA's i18n.language so the editor opens with
// strings that match the rest of the UI.
export async function getWebsiteDesign(
  token: string | null,
  slug: string,
  websiteID: string,
  locale?: string,
): Promise<WebsiteDesign> {
  const qs = locale ? `?locale=${encodeURIComponent(locale)}` : '';
  return apiFetch<WebsiteDesign>(`${designBase(slug, websiteID)}${qs}`, { token });
}

export async function setDesignMode(
  token: string | null,
  slug: string,
  websiteID: string,
  enabled: boolean,
): Promise<{ design_mode_enabled: boolean }> {
  return apiFetch(`${designBase(slug, websiteID)}/mode`, {
    method: 'PATCH',
    body: { enabled },
    token,
  });
}

export async function setDesignSiteEntries(
  token: string | null,
  slug: string,
  websiteID: string,
  entries: SiteEntry[],
): Promise<{ entries: SiteEntry[] }> {
  return apiFetch(`${designBase(slug, websiteID)}/site-entries`, {
    method: 'PUT',
    body: { entries },
    token,
  });
}

export async function setDesignTheme(
  token: string | null,
  slug: string,
  websiteID: string,
  theme: ThemeOverrides,
): Promise<ThemeOverrides> {
  return apiFetch(`${designBase(slug, websiteID)}/theme`, {
    method: 'PUT',
    body: theme,
    token,
  });
}

export async function setDesignModules(
  token: string | null,
  slug: string,
  websiteID: string,
  modules: WebsiteModule[],
): Promise<{ modules: WebsiteModule[] }> {
  return apiFetch(`${designBase(slug, websiteID)}/modules`, {
    method: 'PUT',
    body: { modules },
    token,
  });
}

export async function setDesignLanguages(
  token: string | null,
  slug: string,
  websiteID: string,
  supported: string[],
  primary: string,
): Promise<{ supported_languages: string[]; primary_language: string }> {
  return apiFetch(`${designBase(slug, websiteID)}/languages`, {
    method: 'PUT',
    body: { supported_languages: supported, primary_language: primary },
    token,
  });
}

export async function setDesignPartners(
  token: string | null,
  slug: string,
  websiteID: string,
  partners: Partner[],
): Promise<{ partners: Partner[] }> {
  return apiFetch(`${designBase(slug, websiteID)}/partners`, {
    method: 'PUT',
    body: { partners },
    token,
  });
}

// setDesignContentTranslations writes the multilingual page-copy variants
// (per-language about + events) plus the source language they're translated
// from. An empty translations map clears all translations (single-language
// site). Edits stage to pending and go live on the next publish.
export async function setDesignContentTranslations(
  token: string | null,
  slug: string,
  websiteID: string,
  sourceLanguage: string,
  translations: Record<string, SeedContentTranslation>,
): Promise<{ source_language: string; translations: Record<string, SeedContentTranslation> }> {
  return apiFetch(`${designBase(slug, websiteID)}/content-translations`, {
    method: 'PUT',
    body: { source_language: sourceLanguage, translations },
    token,
  });
}

// setDesignTeams writes the per-site team allowlist. Empty array clears
// the list (the site renders no teams); populated narrows the eligible
// pool to those ids.
export async function setDesignTeams(
  token: string | null,
  slug: string,
  websiteID: string,
  teamIDs: string[],
): Promise<{ team_ids: string[] }> {
  return apiFetch(`${designBase(slug, websiteID)}/teams`, {
    method: 'PUT',
    body: { team_ids: teamIDs },
    token,
  });
}

// setDesignFeaturedTiles writes the magazine spotlight tile grid.
export async function setDesignFeaturedTiles(
  token: string | null,
  slug: string,
  websiteID: string,
  tiles: FeaturedTile[],
): Promise<{ featured_tiles: FeaturedTile[] }> {
  return apiFetch(`${designBase(slug, websiteID)}/featured-tiles`, {
    method: 'PUT',
    body: { featured_tiles: tiles },
    token,
  });
}

// --- Custom domains --------------------------------------------------

// RecommendedRecord mirrors internal/customdomain.RecommendedRecord — one
// DNS record the customer should add at their provider.
export interface RecommendedRecord {
  type: 'CNAME' | 'A';
  name: string;
  value: string;
  ttl: number;
  note?: string;
}

export type CustomDomainStatus = 'pending' | 'verified';

// CustomDomain mirrors the api.domainItem JSON shape.
export interface CustomDomain {
  id: string;
  hostname: string;
  status: CustomDomainStatus;
  dns_provider_id?: string;
  dns_provider_name?: string;
  dns_help_url?: string;
  records: RecommendedRecord[];
  last_checked_at?: string;
  verified_at?: string;
  created_at: string;
}

// DomainDetection mirrors api.detectDomainResponse — the stateless probe the
// wizard runs as the user types, before committing the domain.
export interface DomainDetection {
  domain: string;
  nameservers: string[];
  dns_provider?: { id: string; name: string; dns_help_url: string };
  records: RecommendedRecord[];
}

// VerifyCheck mirrors customdomain.VerifyResult.
export interface VerifyCheck {
  verified: boolean;
  expected: string;
  found: string[];
}

const websiteBase = (slug: string, websiteID: string): string =>
  `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}`;

// detectDomain runs the read-only NS probe: provider + recommended records
// for a typed hostname. No persistence.
export async function detectDomain(
  token: string | null,
  slug: string,
  domain: string,
): Promise<DomainDetection> {
  return apiFetch(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/detect-domain`,
    { method: 'POST', body: { domain }, token },
  );
}

export async function listCustomDomains(
  token: string | null,
  slug: string,
  websiteID: string,
): Promise<{ domains: CustomDomain[] }> {
  return apiFetch(`${websiteBase(slug, websiteID)}/domains`, { token });
}

// connectCustomDomain persists a domain. 409 (ApiError.code === 'domain_taken')
// when the hostname is already connected; 400 ('invalid_domain') when malformed.
export async function connectCustomDomain(
  token: string | null,
  slug: string,
  websiteID: string,
  hostname: string,
): Promise<CustomDomain> {
  return apiFetch(`${websiteBase(slug, websiteID)}/domains`, {
    method: 'POST',
    body: { hostname },
    token,
  });
}

export async function verifyCustomDomain(
  token: string | null,
  slug: string,
  websiteID: string,
  domainID: string,
): Promise<{ domain: CustomDomain; check: VerifyCheck }> {
  return apiFetch(`${websiteBase(slug, websiteID)}/domains/${encodeURIComponent(domainID)}/verify`, {
    method: 'POST',
    token,
  });
}

export async function disconnectCustomDomain(
  token: string | null,
  slug: string,
  websiteID: string,
  domainID: string,
): Promise<void> {
  await apiFetch(`${websiteBase(slug, websiteID)}/domains/${encodeURIComponent(domainID)}`, {
    method: 'DELETE',
    token,
  });
}

// setDesignSlug renames the website's public URL. Server returns 409
// (ApiError.code === 'slug_taken') when another site holds the slug.
export async function setDesignSlug(
  token: string | null,
  slug: string,
  websiteID: string,
  newSlug: string,
): Promise<{ slug: string }> {
  return apiFetch(`${designBase(slug, websiteID)}/slug`, {
    method: 'PUT',
    body: { slug: newSlug },
    token,
  });
}

// --- AI page generation ----------------------------------------------

// generateWebsitePage asks Bedrock to draft a PageContent from a free-
// text brief. Returns a fully-shaped page payload (slug, title, blocks);
// the SPA wraps it as a kind=page SiteEntry and renders the result into
// the editor for the admin to review and commit via the normal save
// path. Throws ApiError 503 when no AI client is configured — callers
// should hide the trigger button.
export async function generateWebsitePage(
  token: string | null,
  slug: string,
  websiteID: string,
  brief: string,
  locale?: string,
): Promise<PageContent> {
  return apiFetch<PageContent>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/generate-page`,
    {
      method: 'POST',
      body: { brief, ...(locale ? { locale } : {}) },
      token,
    },
  );
}

// --- Alt-text suggestion ---------------------------------------------

export interface AltSuggestion {
  alt: string;
  caption: string;
}

// suggestAlt asks Bedrock to look at the just-uploaded image and
// suggest screen-reader alt text + a public caption. The endpoint
// returns 503 when the AI service isn't configured — callers should
// catch ApiError with status === 503 and hide the suggest UI rather
// than logging the failure.
export async function suggestAlt(
  token: string | null,
  slug: string,
  websiteID: string,
  objectKey: string,
  locale?: string,
): Promise<AltSuggestion> {
  return apiFetch<AltSuggestion>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/suggest-alt`,
    {
      method: 'POST',
      body: { object_key: objectKey, ...(locale ? { locale } : {}) },
      token,
    },
  );
}

// --- Version history -------------------------------------------------

export interface WebsiteVersion {
  version: number;
  summary: string;
  actor_user_id?: string;
  actor_type: 'user' | 'ai' | 'worker' | 'system';
  reverted_from?: number;
  created_at: string;
}

export interface VersionsPage {
  versions: WebsiteVersion[];
  total: number;
}

export async function listWebsiteVersions(
  token: string | null,
  slug: string,
  websiteID: string,
  opts: { offset?: number; limit?: number } = {},
): Promise<VersionsPage> {
  const offset = opts.offset ?? 0;
  const limit = opts.limit ?? 50;
  return apiFetch<VersionsPage>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/versions?offset=${offset}&limit=${limit}`,
    { token },
  );
}

// WebsiteVersionDetail mirrors the GET /versions/{n} payload — the same
// metadata as the list rows PLUS the full stored snapshot. The Preview
// modal renders the snapshot field-by-field; the raw shape is the
// snapshot field on websites.org_website_version, validated server-side
// against ValidateSiteEntries / ValidateThemeOverrides at write time.
export interface WebsiteVersionDetail extends WebsiteVersion {
  snapshot: VersionSnapshot;
}

export interface VersionSnapshot {
  name: string;
  template: string;
  hero_seed: number;
  enabled_modules: string[];
  seed_content: SeedContent;
  status: WebsiteStatus;
  design_mode_enabled: boolean;
  site_entries: SiteEntry[];
  theme_overrides: ThemeOverrides;
}

export async function getWebsiteVersion(
  token: string | null,
  slug: string,
  websiteID: string,
  version: number,
): Promise<WebsiteVersionDetail> {
  return apiFetch<WebsiteVersionDetail>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/versions/${version}`,
    { token },
  );
}

export async function revertWebsiteVersion(
  token: string | null,
  slug: string,
  websiteID: string,
  version: number,
): Promise<{ reverted_to: number }> {
  return apiFetch(
    `/api/v1/organizations/${encodeURIComponent(slug)}/websites/${encodeURIComponent(websiteID)}/versions/${version}/revert`,
    { method: 'POST', token },
  );
}
